import { Injectable, inject } from '@angular/core'
import { OidcSecurityService } from 'angular-auth-oidc-client'
import { mergeMap, take, tap, map, finalize } from 'rxjs/operators'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { NgxSpinnerService } from 'ngx-spinner'

import { UserProfile } from '../models/user-profile.model'
import { ApiService } from '../../modules/manage/sequential/services/api.service'
import { mountRouter, userData } from 'app/core/services/app-loader.service'

export function isRestorablePath(pathname: string): boolean {
    return !/^\/(?:not-found|forbidden|unauthorized|error|auto-logout|auto-login|post-logout)(?:\/|$)/.test(pathname)
}

@Injectable()
export class UserService {
    private api = inject(ApiService)
    private oidcSecurityService = inject(OidcSecurityService)
    private spinner = inject(NgxSpinnerService)

    private currentUserSubject = new BehaviorSubject<UserProfile | null>(null)
    currentUser$ = this.currentUserSubject.asObservable()
    private _isAuthorized = false
    private _sessionEnded = false
    private _sessionCookieWatch: ReturnType<typeof setInterval> | null = null

    get sessionEnded(): boolean {
        return this._sessionEnded
    }

    /**
     * Loads the users information and permissions from the API
     * If already loaded, will return the user profile stored in memory
     * */
    loadUserProfile(): Observable<UserProfile> {
        const userDataLoaded = userData.value
        if (this.currentUserSubject.value == null) {
            const getUserData$ = this.oidcSecurityService.userData$.pipe(
                take(1),
                tap(() => this.spinner.show(userDataLoaded?.userData)),
                mergeMap(() => this.api.getUserProfile(userDataLoaded?.userData?.sub)),
                map((userProfileResponse) => {
                    const userProfile: UserProfile = { ...userProfileResponse }
                    return userProfile
                }),
                tap((userProfile) => {
                    this.currentUserSubject.next(userProfile)
                }),
                finalize(() => this.spinner.hide())
            )
            return this.oidcSecurityService.getUserData().pipe(mergeMap((_gotUserInfo) => getUserData$))
        } else {
            const userProfile = this.currentUserSubject.value
            return of(userProfile)
        }
    }

    loadUserInfo(loginResponse): Observable<UserProfile> {
        userData.next(loginResponse)

        const getUserData$ = this.oidcSecurityService.userData$.pipe(
            take(1),
            mergeMap(() => this.api.getUserProfile(loginResponse.userData.sub)),
            map((userProfileResponse) => {
                const userProfile: UserProfile = { ...userProfileResponse }
                return userProfile
            }),
            tap((userProfile) => {
                this.currentUserSubject.next(userProfile)
                this.writeCookie(this.getCookieData(), 'yes')
                this._isAuthorized = true
                this._sessionEnded = false
            }),
            finalize(() => this.spinner.hide())
        )
        return this.oidcSecurityService.getUserData().pipe(
            mergeMap((_gotUserInfo) => getUserData$),
            tap({
                next: () => {
                    const cookieLogin = this.getCookieData('IsNewLogin')
                    if (this.getCookie(cookieLogin.name) === 'yes') {
                        this.writeCookie(cookieLogin, '', true)
                        localStorage.setItem('auctionsMuted', '[]')
                    }
                },
                error: () => this.stopSessionCookieWatch()
            })
        )
    }

    logOut() {
        this.stopSessionCookieWatch()
        this._isAuthorized = false
        this._sessionEnded = true
        this.currentUserSubject.next(null)
        userData.next('')
        this.oidcSecurityService.logoff().subscribe()
        this.clearAuthenticatedCookie()

        localStorage.setItem('auctionsMuted', '[]')
    }

    startSessionCookieWatch() {
        if (this._sessionCookieWatch !== null || !this._isAuthorized || this._sessionEnded) return

        this._sessionCookieWatch = setInterval(() => {
            const cookie = this.getCookieData()
            if (this._isAuthorized && this.getCookie(cookie.name) !== 'yes') {
                this.endSessionAndReauthenticate()
            }
        }, 5000)
    }

    private stopSessionCookieWatch() {
        if (this._sessionCookieWatch !== null) {
            clearInterval(this._sessionCookieWatch)
            this._sessionCookieWatch = null
        }
    }

    endSessionAndReauthenticate() {
        if (this._sessionEnded) return
        this._sessionEnded = true
        this.stopSessionCookieWatch()
        this._isAuthorized = false
        mountRouter.next(false)
        this.currentUserSubject.next(null)
        userData.next('')
        this.oidcSecurityService.logoffLocal()
        this.clearAuthenticatedCookie()
        if (isRestorablePath(window.location.pathname)) {
            localStorage.setItem('fromUrl', window.location.pathname)
        } else {
            localStorage.removeItem('fromUrl')
        }
        this.oidcSecurityService.authorize()
    }

    private clearAuthenticatedCookie() {
        this.writeCookie(this.getCookieData(), '', true)
    }

    /**
     * Chromium rejects Domain=localhost, so local uses a host-only cookie (no Domain attribute).
     * Shared parent-domain cookies still apply for qa/uat/prod.
     */
    private writeCookie(cookie: { name: string; domain: string }, value: string, expire = false) {
        const expires = expire ? ';expires=Thu, 01 Jan 1970 00:00:01 GMT' : ''
        const domainAttr = cookie.domain ? `;domain=${cookie.domain}` : ''
        document.cookie = `${cookie.name}=${value};path=/${domainAttr}${expires}`
    }

    getCookie(name: string): string {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
        if (match) return match[2]
        return ''
    }

    getCookieData(name: string = 'isAuthenticated') {
        const cookie = {
            name: name,
            domain: this.getCookieDomain()
        }

        if (location.host.includes('localhost')) {
            cookie.name += '_dev'
            // Host-only: Domain=localhost is ignored by Chromium and the cookie never sticks.
            cookie.domain = ''
        }

        if (location.host.includes('.qa.')) {
            cookie.name += '_qa'
        }

        if (location.host.includes('.uat.')) {
            cookie.name += '_uat'
        }

        return cookie
    }

    getCookieDomain() {
        const host = location.host.replace('wwww.', '')
        let domain = host.split('.')

        const dotArray: Array<string> = []
        dotArray.push('')

        if (domain.length > 3) {
            domain = domain.splice(1, domain.length)
            return dotArray.concat(domain).join('.')
        }

        if (!host.includes('localhost')) {
            return dotArray.concat(domain).join('.')
        }

        return domain.join('.')
    }
}
